import { FC, PropsWithChildren, useState } from 'react'
import { useIntl } from 'react-intl'
import { generatePath, useParams } from 'react-router'
import { Navigate } from 'react-router-dom'

import { Flex } from 'antd'

import { useQuery } from '@tanstack/react-query'

import { Page } from '../../components/page'
import routes from '../../routes'
import { client } from '../../services/motogp'

import { Context } from './Context'
import { ContextProps } from './index'
import { StandingsForm } from './StandingsForm'
import { StandingsView } from './StandingsView'

const StateHolder: FC<PropsWithChildren<{ categoryId: string; seasonId: string; view: 'table' | 'chart' }>> = ({
  categoryId,
  seasonId,
  view,
  children,
}) => {
  const params = useParams()
  const [state, setState] = useState<ContextProps['state']>({
    categoryId,
    seasonId,
    view,
    params,
  })

  return <Context.Provider value={{ state, setState }}>{children}</Context.Provider>
}

const ParamsContext: FC<
  PropsWithChildren<ContextProps['state']['params']> & { seasonId?: string; view: 'table' | 'chart' }
> = ({ children, seasonId = '', view, ...params }) => {
  const categoriesQuery = useQuery({
    queryKey: ['categories', seasonId],
    queryFn: () => client.categories(seasonId),
    enabled: !!seasonId,
  })

  const categoryId =
    categoriesQuery?.data?.find((it) => it.name.toLowerCase().startsWith(params.category ?? ''))?.id ?? ''

  if (!categoryId || !seasonId) return null

  return (
    <StateHolder seasonId={seasonId} categoryId={categoryId} view={view}>
      {children}
    </StateHolder>
  )
}

const translations: Record<string, string> = {
  ['250cc']: '250cc',
  ['125cc']: '125cc',
  ['50cc']: '50cc',
  ['500cc']: '500cc',
  ['race']: 'гонки',
  ['practice']: 'практики',
  ['sprint']: 'спринту',
  ['warm-up']: 'warmup',
  ['qualification']: 'кваліфікації',
  ['free-practice']: 'практик',
  ['motogp']: 'МотоGP',
  ['moto2']: 'Мото2',
  ['moto3']: 'Мото3',
}

const Standings: FC = () => {
  const { year, category } = useParams<ContextProps['state']['params']>()
  const { data, isLoading: loading } = useQuery({ queryKey: ['seasons'], queryFn: () => client.seasons() })
  const { formatMessage } = useIntl()

  if (!year || !category) return <Navigate to={generatePath(routes.standing, { category: 'motogp', year: '2025' })} />

  const seasonId = data?.find((it) => it.year === Number(year))?.id

  const title = [formatMessage({ id: 'standings.header' }), translations[category], year].join(' ')

  return (
    <Page title={title} loading={loading} seo={{ metaTitle: [title, 'TochkaGP'].join(' | ') }}>
      <Flex vertical gap={32}>
        <ParamsContext seasonId={seasonId} view={'table'} category={category} year={year}>
          <StandingsForm />
          <StandingsView />
        </ParamsContext>
      </Flex>
    </Page>
  )
}

export default Standings
